import { useEffect, useRef, useCallback } from "react";

const LERP = 0.15;
const DOT_SIZE = 8;
const RING_SIZE = 32;
const RING_HOVER_SIZE = 64;

export const CustomCursor = () => {
  const dotRef = useRef<HTMLDivElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);
  const pos = useRef({ x: 0, y: 0 });
  const target = useRef({ x: 0, y: 0 });
  const hovering = useRef(false);
  const labelRef = useRef<HTMLSpanElement>(null);
  const rafId = useRef(0);
  const visible = useRef(false);

  const isTouchDevice = typeof window !== "undefined" && ("ontouchstart" in window || navigator.maxTouchPoints > 0);

  const animate = useCallback(() => {
    pos.current.x += (target.current.x - pos.current.x) * LERP;
    pos.current.y += (target.current.y - pos.current.y) * LERP;

    if (dotRef.current) {
      dotRef.current.style.transform = `translate(${pos.current.x - DOT_SIZE / 2}px, ${pos.current.y - DOT_SIZE / 2}px)`;
    }
    if (ringRef.current) {
      const size = hovering.current ? RING_HOVER_SIZE : RING_SIZE;
      ringRef.current.style.transform = `translate(${pos.current.x - size / 2}px, ${pos.current.y - size / 2}px)`;
      ringRef.current.style.width = `${size}px`;
      ringRef.current.style.height = `${size}px`;
    }

    rafId.current = requestAnimationFrame(animate);
  }, []);

  useEffect(() => {
    if (isTouchDevice || window.innerWidth < 768) return;

    const onMove = (e: MouseEvent) => {
      target.current.x = e.clientX;
      target.current.y = e.clientY;

      if (!visible.current) {
        visible.current = true;
        if (dotRef.current) dotRef.current.style.opacity = "1";
        if (ringRef.current) ringRef.current.style.opacity = "1";
      }
    };

    const onOver = (e: MouseEvent) => {
      const el = e.target as HTMLElement;
      const interactive = el.closest("a, button, [role='button'], input, textarea, select, [data-cursor]");
      hovering.current = !!interactive;

      if (labelRef.current) {
        const label = interactive?.getAttribute("data-cursor") || "";
        labelRef.current.textContent = label;
        labelRef.current.style.opacity = label ? "1" : "0";
      }

      if (ringRef.current) {
        ringRef.current.style.borderColor = hovering.current
          ? "rgba(212, 168, 67, 0.6)"
          : "rgba(212, 168, 67, 0.3)";
      }
    };

    const onLeave = () => {
      visible.current = false;
      if (dotRef.current) dotRef.current.style.opacity = "0";
      if (ringRef.current) ringRef.current.style.opacity = "0";
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseover", onOver);
    document.addEventListener("mouseleave", onLeave);
    rafId.current = requestAnimationFrame(animate);

    document.documentElement.style.cursor = "none";
    const style = document.createElement("style");
    style.textContent = "a,button,input,textarea,select,[role='button']{cursor:none!important}";
    document.head.appendChild(style);

    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseover", onOver);
      document.removeEventListener("mouseleave", onLeave);
      cancelAnimationFrame(rafId.current);
      document.documentElement.style.cursor = "";
      style.remove();
    };
  }, [animate, isTouchDevice]);

  if (isTouchDevice || (typeof window !== "undefined" && window.innerWidth < 768)) return null;

  return (
    <>
      <div
        ref={dotRef}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: DOT_SIZE,
          height: DOT_SIZE,
          borderRadius: "50%",
          backgroundColor: "#D4A843",
          pointerEvents: "none",
          zIndex: 99999,
          opacity: 0,
          transition: "opacity 0.3s",
          willChange: "transform",
        }}
      />
      <div
        ref={ringRef}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: RING_SIZE,
          height: RING_SIZE,
          borderRadius: "50%",
          border: "1.5px solid rgba(212, 168, 67, 0.3)",
          pointerEvents: "none",
          zIndex: 99998,
          opacity: 0,
          transition: "width 0.3s ease, height 0.3s ease, border-color 0.3s ease, opacity 0.3s",
          willChange: "transform",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <span
          ref={labelRef}
          style={{
            fontSize: 10,
            fontWeight: 600,
            color: "#D4A843",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            opacity: 0,
            transition: "opacity 0.2s",
            whiteSpace: "nowrap",
          }}
        />
      </div>
    </>
  );
};
